import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("trf_user") || "null");
        } catch {
            return null;
        }
    });
    const [loading, setLoading] = useState(true);

    // hydrate: valida token con /auth/me
    useEffect(() => {
        const token = localStorage.getItem("trf_token");
        if (!token) {
            setLoading(false);
            return;
        }
        api.get("/auth/me")
            .then((r) => {
                setUser(r.data);
                localStorage.setItem("trf_user", JSON.stringify(r.data));
            })
            .catch(() => {
                localStorage.removeItem("trf_token");
                localStorage.removeItem("trf_user");
                setUser(null);
            })
            .finally(() => setLoading(false));
    }, []);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        return data; // {mfa_token, mfa_enrolled, email}
    }, []);

    const verifyMfa = useCallback(async (mfa_token, code) => {
        const { data } = await api.post("/auth/mfa", { mfa_token, code });
        localStorage.setItem("trf_token", data.access_token);
        localStorage.setItem("trf_user", JSON.stringify(data.user));
        setUser(data.user);
        return data;
    }, []);

    const demoLogin = useCallback(async (email) => {
        const { data } = await api.post("/dev/demo-login", { email });
        localStorage.setItem("trf_token", data.access_token);
        localStorage.setItem("trf_user", JSON.stringify(data.user));
        setUser(data.user);
        return data;
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem("trf_token");
        localStorage.removeItem("trf_user");
        setUser(null);
    }, []);

    const hasRole = useCallback(
        (roles) => {
            if (!user) return false;
            if (!roles || roles.length === 0) return true;
            const set = new Set(user.roles || []);
            return roles.some((r) => set.has(r));
        },
        [user],
    );

    const value = useMemo(
        () => ({ user, loading, login, verifyMfa, demoLogin, logout, hasRole }),
        [user, loading, login, verifyMfa, demoLogin, logout, hasRole],
    );
    return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthCtx);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
